import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import StatCard from '../components/StatCard';
import PlanUpgradeBanner from '../components/PlanUpgradeBanner';
import ClassManager from '../components/ClassManager';
import HolisticCalendar        from '../components/admin/HolisticCalendar';
import AssignmentConfigSettings from '../components/admin/AssignmentConfigSettings';
import AssignmentAnalytics      from '../components/admin/AssignmentAnalytics';
import BillingPage             from './BillingPage';
import { FiCalendar, FiCopy, FiCheck } from 'react-icons/fi';
import { useWindowSize } from '../utils/useWindowSize';
import { healersFromResponse } from '../utils/healersFromResponse';
import { getStats, getSessions, getHealingRequests, getCourseCompletions, getUsers, generatePatientInvoice, adminSettlement, settleHealerPayment, updateCaseTags, globalSearch, getPendingApprovals, approveUser, rejectUser, updateHealerVisibility, getCentreProfile } from '../utils/api';
import api from '../utils/api';
 
const AdminDashboard = () => {
  const { isMobile } = useWindowSize();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [healingRequests, setHealingRequests] = useState([]);
  const [pendingCompletions, setPendingCompletions] = useState([]);
  const [healers, setHealers] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [paymentEdit, setPaymentEdit] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amountReceived: false, amountPaid: 0, healerSharePercent: 0, settledToHealer: false, notes: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [settlementModal, setSettlementModal] = useState(null); // requestId or null
  const [settlementForm, setSettlementForm] = useState({ sessionsClaimed: 1, notes: '', patientNameDisplay: 'full' });
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [settlingPaymentId, setSettlingPaymentId] = useState(null);
  const [settlePatientNameDisplay, setSettlePatientNameDisplay] = useState('full');

  // Promo code state for the payment panel
  const [promoInput,    setPromoInput]    = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoResult,   setPromoResult]   = useState(null);

  // Healing Requests filter state
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [selectedHealers, setSelectedHealers] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filterActive, setFilterActive] = useState(false);
  const [healerDropdownOpen, setHealerDropdownOpen] = useState(false);
 
  // Healer Performance filter state
  const [healerPerfDateFilter, setHealerPerfDateFilter] = useState({ from: '', to: '' });
  const [healerPerfFilterActive, setHealerPerfFilterActive] = useState(false);

  // Global search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState({ users: [], cases: [] });
  const [searchLoading, setSearchLoading] = useState(false);

  // Case tags state
  const [tagEditorOpen, setTagEditorOpen] = useState(null); // requestId
  const [savingTags, setSavingTags] = useState(false);
  const [filterTag, setFilterTag] = useState('');

  // Pending approvals state
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [togglingAvailId, setTogglingAvailId] = useState(null);
  const [directoryListing,   setDirectoryListing]   = useState(null);
  const [listingSubmitting,  setListingSubmitting]  = useState(false);
  const [showListingConfirm, setShowListingConfirm] = useState(false);
  const [centreCode,         setCentreCode]         = useState('');
  const [codeCopied,         setCodeCopied]         = useState(false);

  const PRESET_TAGS = [
    { label: 'Chronic', color: '#8e44ad', bg: '#f5eef8' },
    { label: 'Child', color: '#2980b9', bg: '#eaf4fb' },
    { label: 'Priority', color: '#e74c3c', bg: '#fdf0f0' },
    { label: 'Follow-up', color: '#d4ac0d', bg: '#fef9e7' },
    { label: 'Remote', color: '#4a8f82', bg: '#eafaf1' },
    { label: 'Referred', color: '#e67e22', bg: '#fef5ec' },
    { label: 'Critical Care', color: '#c0392b', bg: '#fdf0f0' },
    { label: 'Elderly', color: '#7f8c8d', bg: '#f2f3f4' },
  ];

  var handleSearch = async function(q) {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults({ users: [], cases: [] }); return; }
    setSearchLoading(true);
    try {
      var userRes = await globalSearch(q);
      var matchedCases = healingRequests.filter(function(r) {
        var pName = r.patient ? (r.patient.firstName + ' ' + r.patient.lastName).toLowerCase() : '';
        var hName = r.assignedHealer ? (r.assignedHealer.firstName + ' ' + r.assignedHealer.lastName).toLowerCase() : '';
        var cond = (r.condition || '').toLowerCase();
        var ql = q.toLowerCase();
        return pName.includes(ql) || hName.includes(ql) || cond.includes(ql);
      }).slice(0, 8);
      setSearchResults({ users: userRes.data.users || [], cases: matchedCases });
    } catch (err) {
      setSearchResults({ users: [], cases: [] });
    } finally {
      setSearchLoading(false);
    }
  };

  var handleToggleTag = async function(req, tag) {
    var current = req.tags || [];
    var next = current.includes(tag) ? current.filter(function(t) { return t !== tag; }) : [...current, tag];
    setSavingTags(true);
    try {
      var res = await updateCaseTags(req._id, next);
      setHealingRequests(function(prev) {
        return prev.map(function(r) { return r._id === req._id ? res.data.data : r; });
      });
    } catch (err) {
      alert('Error saving tags');
    } finally {
      setSavingTags(false);
    }
  };
 
  useEffect(() => { fetchData(); }, []);

  useEffect(function() {
    if (activeTab !== 'settings' || directoryListing !== null) return;
    api.get('/admin/directory/listing')
      .then(function(res) { setDirectoryListing(res.data.data); })
      .catch(function()   { setDirectoryListing({ isListed: false, slug: null }); });
  }, [activeTab, directoryListing]);

  useEffect(function() {
    if (activeTab !== 'settings' || centreCode) return;
    getCentreProfile()
      .then(function(res) { setCentreCode(res.data.data?.centreCode || ''); })
      .catch(function()   { /* non-critical — field stays blank */ });
  }, [activeTab, centreCode]);
 
 // useEffect(() => {
 //   applyFilters();
 // }, [healingRequests, dateFilter, selectedHealers, filterActive]);

useEffect(() => {
  let filtered = healingRequests;
  if (filterActive) {
    if (dateFilter.from || dateFilter.to) {
      filtered = filtered.filter(function(req) {
        const reqDate = new Date(req.requestedAt);
        if (dateFilter.from && reqDate < new Date(dateFilter.from)) return false;
        if (dateFilter.to) {
          const to = new Date(dateFilter.to);
          to.setHours(23, 59, 59, 999);
          if (reqDate > to) return false;
        }
        return true;
      });
    }
    if (selectedHealers.length > 0) {
      filtered = filtered.filter(function(req) {
        if (!req.assignedHealer) return selectedHealers.includes('unassigned');
        return selectedHealers.includes(req.assignedHealer._id);
      });
    }
  }
  if (filterTag) {
    filtered = filtered.filter(function(req) {
      return req.tags && req.tags.includes(filterTag);
    });
  }
  setFilteredRequests(filtered);
}, [healingRequests, dateFilter, selectedHealers, filterActive, filterTag]);
   const fetchData = async () => {
    try {
      // Promise.allSettled — each call resolves independently.
      // One failed endpoint does not blank the entire dashboard.
      const [statsRes, sessionsRes, healingRes, completionsRes, healersRes, usersRes] =
        await Promise.allSettled([
          getStats(),
          getSessions(),
          getHealingRequests(),
          getCourseCompletions(),
          api.get('/admin/users/healers'),
          getUsers()
        ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data.stats);
      else console.error('fetchData: getStats failed —', statsRes.reason?.message);

      if (sessionsRes.status === 'fulfilled') setSessions((sessionsRes.value.data.sessions || sessionsRes.value.data.data || []).slice(0, 5));
      else console.error('fetchData: getSessions failed —', sessionsRes.reason?.message);

      if (healingRes.status === 'fulfilled') {
        setHealingRequests(healingRes.value.data.data);
        setFilteredRequests(healingRes.value.data.data);
      } else console.error('fetchData: getHealingRequests failed —', healingRes.reason?.message);

      if (completionsRes.status === 'fulfilled') setPendingCompletions(completionsRes.value.data.completions.filter(function(c) { return c.status === 'submitted'; }).slice(0, 5));
      else console.error('fetchData: getCourseCompletions failed —', completionsRes.reason?.message);

      if (healersRes.status === 'fulfilled') setHealers(healersFromResponse(healersRes.value));
      else console.error('fetchData: getHealers failed —', healersRes.reason?.message);

      if (usersRes.status === 'fulfilled') setRecentUsers(usersRes.value.data.users.slice(0, 5));
      else console.error('fetchData: getUsers failed —', usersRes.reason?.message);

      try {
        var pendingRes = await getPendingApprovals();
        setPendingApprovals(pendingRes.data.data?.users || []);
      } catch (e) { /* non-critical */ }
    } catch (error) {
      console.error('fetchData: unexpected error —', error);
    } finally {
      setLoading(false);
    }
  };
 
  const handleApplyFilter = function() {
    setFilterActive(true);
    setHealerDropdownOpen(false);
  };
 
  const handleClearFilter = function() {
    setDateFilter({ from: '', to: '' });
    setSelectedHealers([]);
    setFilterTag('');
    setFilterActive(false);
    setFilteredRequests(healingRequests);
  };
 
  const toggleHealerSelection = function(healerId) {
    setSelectedHealers(function(prev) {
      if (prev.includes(healerId)) return prev.filter(function(id) { return id !== healerId; });
      return [...prev, healerId];
    });
  };
 
  const openPaymentEdit = function(req) {
    setPaymentEdit(req._id);
    setPaymentForm({
      amountReceived: req.payment ? req.payment.amountReceived : false,
      amountPaid: req.payment ? req.payment.amountPaid : 0,
      healerSharePercent: req.payment ? req.payment.healerSharePercent : 0,
      settledToHealer: req.payment ? req.payment.settledToHealer : false,
      notes: req.payment ? req.payment.notes : ''
    });
    setPromoInput('');
    setPromoResult(null);
  };

  const savePayment = async function(requestId) {
    setSavingPayment(true);
    try {
      const healerAmount = Math.round((paymentForm.amountPaid * paymentForm.healerSharePercent) / 100);
      await api.put('/healing-requests/' + requestId + '/payment', { ...paymentForm, healerShareAmount: healerAmount });
      setPaymentEdit(null);
      fetchData();
      alert('Payment details saved.');
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error saving payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const applyPromo = async function(requestId) {
    if (!promoInput.trim()) return;
    setApplyingPromo(true);
    setPromoResult(null);
    try {
      const res = await api.post('/healing-requests/' + requestId + '/apply-promo', { promoCode: promoInput.trim().toUpperCase() });
      setPromoResult(res.data.data);
      fetchData();
    } catch (error) {
      setPromoResult({ error: error.response ? error.response.data.message : 'Error applying promo code.' });
    } finally {
      setApplyingPromo(false);
    }
  };

  const apiErrMsg = function(error, fallback) {
    if (!error.response) return error.message || fallback;
    var d = error.response.data;
    return d && d.message ? d.message : (d && d.error ? d.error : 'Server error ' + error.response.status);
  };

  const printPatientInvoice = function(req) {
    var centreName = process.env.REACT_APP_CENTRE_NAME || '';
    var centreAddress = process.env.REACT_APP_CENTRE_ADDRESS || '';
    var centrePhone = process.env.REACT_APP_CENTRE_PHONE || '';
    var centreEmail = process.env.REACT_APP_CENTRE_EMAIL || '';
    var logoUrl = process.env.REACT_APP_CENTRE_LOGO_URL || '';
    var patient = req.patient;
    var patientName = patient ? patient.firstName + ' ' + patient.lastName : (req.manualPatientName || 'Patient');
    var healer = req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : 'Not assigned';
    var amount = (req.payment && req.payment.amountPaid) || 0;
    var billNo = (req.payment && req.payment.billNumber) || ('INV-' + req.requestedAt ? new Date(req.requestedAt).getFullYear() : new Date().getFullYear()) + '-' + req._id.slice(-6).toUpperCase();
    var billDate = req.payment && req.payment.billGeneratedAt ? new Date(req.payment.billGeneratedAt).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN');
    var w = window.open('', '_blank');
    w.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice - ' + patientName + '</title><style>body{font-family:Arial,sans-serif;margin:0;padding:0;color:#333}@media print{.no-print{display:none}}.page{max-width:680px;margin:0 auto;padding:40px}.header{background:linear-gradient(135deg,#071e12,#0c2617);padding:28px 36px;display:flex;justify-content:space-between;align-items:center}.logo{height:44px}.accent{height:3px;background:linear-gradient(to right,#00AEEF,#4a8f82,#8DC63F)}.body{padding:36px}.title-row{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px}.invoice-title{font-size:26px;font-weight:700;color:#1a1a2e;margin:0}.bill-info{text-align:right;font-size:13px;color:#555}.bill-info strong{color:#1a1a2e}.divider{border:none;border-top:1px solid #e0e0e0;margin:20px 0}.table{width:100%;border-collapse:collapse;margin:20px 0}.table th{background:#f9faf7;padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;border-bottom:2px solid #e0e0e0}.table td{padding:12px 14px;border-bottom:1px solid #f0f0f0;font-size:13px}.total-row{background:#071e12;color:#fff}.total-row td{padding:14px;font-weight:700;font-size:15px}.footer{background:#f9faf7;border-top:1px solid #e0e0e0;padding:20px 36px;font-size:11px;color:#888;text-align:center}.print-btn{background:#4a8f82;color:#fff;border:none;padding:12px 28px;border-radius:6px;font-size:14px;font-weight:700;cursor:pointer;margin-bottom:20px}</style></head><body>');
    w.document.write('<div class="page">');
    w.document.write('<button class="no-print print-btn" onclick="window.print()">Print / Save as PDF</button>');
    w.document.write('<div class="header"><img class="logo" src="' + logoUrl + '" alt="' + centreName + '"><div style="text-align:right;color:rgba(255,255,255,0.7);font-size:12px"><div style="color:#8DC63F;font-weight:700;font-size:14px;margin-bottom:4px">RECEIPT</div>' + centreName + '</div></div>');
    w.document.write('<div class="accent"></div>');
    w.document.write('<div class="body">');
    w.document.write('<div class="title-row"><div><div class="invoice-title">Payment Receipt</div><div style="color:#666;font-size:13px;margin-top:6px">Pranic Healing Treatment</div></div><div class="bill-info"><div><strong>Bill No:</strong> ' + billNo + '</div><div><strong>Date:</strong> ' + billDate + '</div></div></div>');
    w.document.write('<hr class="divider">');
    w.document.write('<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:24px">');
    w.document.write('<div><div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:8px">Bill To</div><div style="font-weight:700;font-size:15px;color:#1a1a2e">' + patientName + '</div>' + (patient && patient.email ? '<div style="font-size:12px;color:#666">' + patient.email + '</div>' : '') + (patient && patient.phone ? '<div style="font-size:12px;color:#666">' + patient.phone + '</div>' : '') + '</div>');
    w.document.write('<div><div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:8px">Service Provider</div><div style="font-weight:700;font-size:15px;color:#1a1a2e">' + centreName + '</div><div style="font-size:12px;color:#666">' + centreAddress + '</div><div style="font-size:12px;color:#666">' + centrePhone + '</div></div>');
    w.document.write('</div>');
    w.document.write('<table class="table"><thead><tr><th>Description</th><th>Healer</th><th>Sessions</th><th style="text-align:right">Amount</th></tr></thead><tbody>');
    w.document.write('<tr><td><strong>' + req.condition + '</strong><br><span style="color:#888;font-size:12px">Pranic Healing Treatment' + (req.assignedAt ? ' | Started ' + new Date(req.assignedAt).toLocaleDateString("en-IN") : '') + '</span></td><td>' + healer + '</td><td>' + (req.totalSessionsCompleted || 0) + ' sessions</td><td style="text-align:right;font-weight:700">₹' + amount.toLocaleString('en-IN') + '</td></tr>');
    w.document.write('</tbody><tfoot><tr class="total-row"><td colspan="3" style="text-align:right;color:rgba(255,255,255,0.7)">Total Received</td><td style="text-align:right;color:#8DC63F">₹' + amount.toLocaleString('en-IN') + '</td></tr></tfoot></table>');
    w.document.write('<div style="background:#eafaf1;border:1px solid #a9dfbf;border-radius:6px;padding:12px 16px;font-size:12px;color:#1a5c38;margin-top:16px">✓ Payment Received — Thank you for choosing ' + centreName + ' for your healing journey.</div>');
    w.document.write('</div>');
    w.document.write('<div class="footer">' + centreName + ' | ' + centreAddress + ' | ' + centrePhone + ' | ' + centreEmail + '<br>This is a computer-generated receipt and does not require a signature.</div>');
    w.document.write('</div></body></html>');
    w.document.close();
  };

  const printHealerStatement = function(req) {
    var centreName = process.env.REACT_APP_CENTRE_NAME || '';
    var centreAddress = process.env.REACT_APP_CENTRE_ADDRESS || '';
    var centrePhone = process.env.REACT_APP_CENTRE_PHONE || '';
    var logoUrl = process.env.REACT_APP_CENTRE_LOGO_URL || '';
    var healer = req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : 'Healer';
    var patientName = req.patient ? req.patient.firstName + ' ' + req.patient.lastName : (req.manualPatientName || 'Patient');
    var payments = req.healerPayments || [];
    var totalSettled = payments.filter(function(p) { return p.status === 'settled'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);
    var totalEntitlement = (req.payment && req.payment.healerShareAmount) || 0;
    var w = window.open('', '_blank');
    w.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Healer Statement - ' + healer + '</title><style>body{font-family:Arial,sans-serif;margin:0;padding:0;color:#333}@media print{.no-print{display:none}}.page{max-width:680px;margin:0 auto;padding:40px}.header{background:linear-gradient(135deg,#071e12,#0c2617);padding:28px 36px;display:flex;justify-content:space-between;align-items:center}.logo{height:44px}.accent{height:3px;background:linear-gradient(to right,#00AEEF,#4a8f82,#8DC63F)}.body{padding:36px}.divider{border:none;border-top:1px solid #e0e0e0;margin:20px 0}.table{width:100%;border-collapse:collapse;margin:20px 0}.table th{background:#f9faf7;padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;border-bottom:2px solid #e0e0e0}.table td{padding:12px 14px;border-bottom:1px solid #f0f0f0;font-size:13px}.settled{color:#27ae60;font-weight:700}.pending{color:#d4ac0d;font-weight:700}.total-row{background:#071e12;color:#fff}.total-row td{padding:14px;font-weight:700;font-size:15px}.footer{background:#f9faf7;border-top:1px solid #e0e0e0;padding:20px 36px;font-size:11px;color:#888;text-align:center}.print-btn{background:#4a8f82;color:#fff;border:none;padding:12px 28px;border-radius:6px;font-size:14px;font-weight:700;cursor:pointer;margin-bottom:20px}</style></head><body>');
    w.document.write('<div class="page">');
    w.document.write('<button class="no-print print-btn" onclick="window.print()">Print / Save as PDF</button>');
    w.document.write('<div class="header"><img class="logo" src="' + logoUrl + '" alt="' + centreName + '"><div style="text-align:right;color:rgba(255,255,255,0.7);font-size:12px"><div style="color:#8DC63F;font-weight:700;font-size:14px;margin-bottom:4px">PAYMENT STATEMENT</div>' + centreName + '</div></div>');
    w.document.write('<div class="accent"></div>');
    w.document.write('<div class="body">');
    w.document.write('<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px"><div><div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px">Healer</div><div style="font-weight:700;font-size:18px;color:#1a1a2e">' + healer + '</div>' + (req.assignedHealer && req.assignedHealer.email ? '<div style="font-size:12px;color:#666">' + req.assignedHealer.email + '</div>' : '') + '</div><div style="text-align:right"><div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px">Case</div><div style="font-weight:700;color:#1a1a2e">' + req.condition + '</div><div style="font-size:12px;color:#666">Patient: ' + patientName + '</div><div style="font-size:12px;color:#666">Date: ' + new Date().toLocaleDateString("en-IN") + '</div></div></div>');
    w.document.write('<hr class="divider">');
    w.document.write('<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:24px;background:#f9faf7;padding:16px;border-radius:8px">');
    w.document.write('<div style="text-align:center"><div style="font-size:22px;font-weight:700;color:#4a8f82">' + (req.totalSessionsCompleted || 0) + '</div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px">Sessions Done</div></div>');
    w.document.write('<div style="text-align:center"><div style="font-size:22px;font-weight:700;color:#1a1a2e">₹' + totalEntitlement.toLocaleString('en-IN') + '</div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px">Total Entitlement</div></div>');
    w.document.write('<div style="text-align:center"><div style="font-size:22px;font-weight:700;color:#27ae60">₹' + totalSettled.toLocaleString('en-IN') + '</div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px">Total Settled</div></div>');
    w.document.write('</div>');
    if (payments.length > 0) {
      w.document.write('<table class="table"><thead><tr><th>Invoice #</th><th>Sessions</th><th>Amount</th><th>Raised On</th><th>Status</th><th>Settled On</th></tr></thead><tbody>');
      payments.forEach(function(hp) {
        w.document.write('<tr><td><strong>' + (hp.paymentNumber || '-') + '</strong>' + (hp.raisedBy === 'admin' ? '<br><span style="font-size:11px;color:#888">(Admin)</span>' : '') + '</td><td>' + (hp.sessionsClaimed || 0) + '</td><td style="font-weight:700">₹' + (hp.amountClaimed || 0).toLocaleString('en-IN') + '</td><td>' + new Date(hp.raisedAt).toLocaleDateString('en-IN') + '</td><td class="' + hp.status + '">' + hp.status.toUpperCase() + '</td><td>' + (hp.settledAt ? new Date(hp.settledAt).toLocaleDateString('en-IN') : '—') + '</td></tr>');
      });
      var pending = totalEntitlement - totalSettled;
      w.document.write('</tbody><tfoot><tr class="total-row"><td colspan="2" style="color:rgba(255,255,255,0.7)">Total Settled</td><td style="color:#8DC63F">₹' + totalSettled.toLocaleString('en-IN') + '</td><td colspan="2" style="color:rgba(255,255,255,0.7)">Pending</td><td style="color:' + (pending > 0 ? '#f0c040' : '#8DC63F') + '">₹' + pending.toLocaleString('en-IN') + '</td></tr></tfoot></table>');
    } else {
      w.document.write('<div style="background:#f9faf7;padding:20px;text-align:center;color:#999;border-radius:8px">No payment records yet.</div>');
    }
    w.document.write('</div>');
    w.document.write('<div class="footer">' + centreName + ' | ' + centreAddress + ' | ' + centrePhone + '<br>This is a computer-generated statement.</div>');
    w.document.write('</div></body></html>');
    w.document.close();
  };

  const handleGeneratePatientInvoice = async function(requestId) {
    setGeneratingInvoice(true);
    try {
      await generatePatientInvoice(requestId);
      fetchData();
      alert('Invoice generated and emailed to patient.');
    } catch (error) {
      alert(apiErrMsg(error, 'Error generating invoice'));
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleAdminSettlement = async function(requestId) {
    setSavingSettlement(true);
    try {
      await adminSettlement(requestId, settlementForm);
      setSettlementModal(null);
      setSettlementForm({ sessionsClaimed: 1, notes: '', patientNameDisplay: 'full' });
      fetchData();
      alert('Settlement recorded and statement emailed to healer.');
    } catch (error) {
      alert(apiErrMsg(error, 'Error recording settlement'));
    } finally {
      setSavingSettlement(false);
    }
  };

  const handleSettleHealerPayment = async function(requestId, paymentId) {
    setSettlingPaymentId(paymentId);
    try {
      await settleHealerPayment(requestId, paymentId, { patientNameDisplay: settlePatientNameDisplay });
      fetchData();
      alert('Payment marked settled. Statement emailed to healer.');
    } catch (error) {
      alert(apiErrMsg(error, 'Error settling payment'));
    } finally {
      setSettlingPaymentId(null);
    }
  };

  // Healer stats calculator — supports date filter
  const getHealerStats = function(healerId) {
    let requests = healingRequests.filter(function(r) { return r.assignedHealer && r.assignedHealer._id === healerId; });
 
    if (healerPerfFilterActive && (healerPerfDateFilter.from || healerPerfDateFilter.to)) {
      requests = requests.filter(function(r) {
        const d = new Date(r.requestedAt);
        if (healerPerfDateFilter.from && d < new Date(healerPerfDateFilter.from)) return false;
        if (healerPerfDateFilter.to) {
          const to = new Date(healerPerfDateFilter.to);
          to.setHours(23, 59, 59, 999);
          if (d > to) return false;
        }
        return true;
      });
    }
 
    const completed = requests.filter(function(r) { return r.status === 'completed'; });
    const active = requests.filter(function(r) { return r.status === 'assigned' || r.status === 'in_progress'; });
    const totalSessions = requests.reduce(function(sum, r) { return sum + (r.totalSessionsCompleted || 0); }, 0);
    const revenueGenerated = requests.reduce(function(sum, r) {
      return sum + (r.payment && r.payment.amountReceived ? (r.payment.amountPaid || 0) : 0);
    }, 0);
    const paidToHealer = requests.reduce(function(sum, r) {
      return sum + (r.healerPayments || []).filter(function(p) { return p.status === 'settled'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);
    }, 0);
 
    return { total: requests.length, completed: completed.length, active: active.length, totalSessions, revenueGenerated, paidToHealer };
  };
 
  const pendingRequests = filteredRequests.filter(function(r) { return r.status === 'pending' && r.assignment_status !== 'returned'; });
  const activeRequests = filteredRequests.filter(function(r) { return r.status === 'assigned' || r.status === 'in_progress'; });
  const completedRequests = filteredRequests.filter(function(r) { return r.status === 'completed'; });
 
  const totalRevenue = filteredRequests.reduce(function(sum, r) {
    return sum + (r.payment && r.payment.amountReceived ? (r.payment.amountPaid || 0) : 0);
  }, 0);
 
  const totalPaidToHealers = filteredRequests.reduce(function(sum, r) {
    return sum + (r.healerPayments || []).filter(function(p) { return p.status === 'settled'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);
  }, 0);
 
  const statusColors = {
    pending: { bg: '#fef9e7', color: '#d4ac0d' },
    assigned: { bg: '#eaf4fb', color: '#2980b9' },
    in_progress: { bg: '#eafaf1', color: '#27ae60' },
    completed: { bg: '#f0f0f0', color: '#666' }
  };
 
  const urgencyColors = {
    normal: { bg: '#eafaf1', color: '#27ae60' },
    urgent: { bg: '#fef9e7', color: '#d4ac0d' },
    critical: { bg: '#fdf0f0', color: '#e74c3c' }
  };
 
  const roleColors = {
    admin: { bg: '#fdf0f0', color: '#e74c3c' },
    healer: { bg: '#fef9e7', color: '#d4ac0d' },
    student: { bg: '#eafaf1', color: '#27ae60' },
    patient: { bg: '#eaf4fb', color: '#2980b9' },
    general: { bg: '#f0f0f0', color: '#666' }
  };
 
  const healerShareAmount = Math.round((paymentForm.amountPaid * paymentForm.healerSharePercent) / 100);
 
  const selectedHealerNames = selectedHealers.map(function(id) {
    if (id === 'unassigned') return 'Unassigned';
    const h = healers.find(function(h) { return h._id === id; });
    return h ? h.firstName + ' ' + h.lastName : '';
  }).filter(Boolean);

  var s = {
    ...styles,
    header: { ...styles.header, ...(isMobile ? { flexDirection: 'column', gap: '16px' } : {}) },
    statsGrid: { ...styles.statsGrid, ...(isMobile ? { gridTemplateColumns: 'repeat(2, 1fr)' } : {}) },
    tabs: { ...styles.tabs, ...(isMobile ? { overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' } : {}) },
    contentGrid: { ...styles.contentGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) },
    filterBar: { ...styles.filterBar, ...(isMobile ? { flexDirection: 'column', alignItems: 'flex-start', gap: '16px' } : {}) },
    filterStats: { ...styles.filterStats, ...(isMobile ? { flexWrap: 'wrap', gap: '12px' } : {}) },
    requestHeader: { ...styles.requestHeader, ...(isMobile ? { flexDirection: 'column', gap: '12px' } : {}) },
    requestInfoRow: { ...styles.requestInfoRow, ...(isMobile ? { gridTemplateColumns: 'repeat(2, 1fr)' } : {}) },
    requestActions: { ...styles.requestActions, ...(isMobile ? { flexWrap: 'wrap' } : {}) },
    paymentGrid: { ...styles.paymentGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) },
    healerGrid: { ...styles.healerGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) },
    healerPerfFilterBar: { ...styles.healerPerfFilterBar, ...(isMobile ? { flexDirection: 'column', alignItems: 'flex-start', gap: '16px' } : {}) },
    sessionItem: { ...styles.sessionItem, ...(isMobile ? { gridTemplateColumns: '1fr', gap: '8px' } : {}) },
    table: { ...styles.table, ...(isMobile ? { display: 'block', overflowX: 'auto', WebkitOverflowScrolling: 'touch' } : {}) },
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <div style={{ ...s.main, marginLeft: isMobile ? 0 : '260px', padding: isMobile ? '56px 16px 16px' : '32px', minWidth: 0, overflowX: 'hidden' }}>
 
        <div style={s.header}>
          <div>
            <h1 style={s.headerTitle}>{process.env.REACT_APP_PORTAL_LABEL}</h1>
            <p style={s.headerSub}>{process.env.REACT_APP_CENTRE_NAME} — Administration</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
            {/* Global Search */}
            <div style={{ position: 'relative', flex: isMobile ? 1 : 'none' }}>
              <input
                value={searchQuery}
                onChange={function(e) { handleSearch(e.target.value); setSearchOpen(true); }}
                onFocus={function() { setSearchOpen(true); }}
                placeholder="Search patients, healers, cases..."
                style={{ padding: '9px 14px 9px 36px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', width: isMobile ? '100%' : '280px', boxSizing: 'border-box', fontFamily: "'Open Sans', sans-serif", background: '#f9faf7', outline: 'none' }}
              />
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#bbb', fontSize: '14px' }}>🔍</span>
              {searchOpen && searchQuery && (
                <>
                  <div onClick={function() { setSearchOpen(false); setSearchQuery(''); setSearchResults({ users: [], cases: [] }); }} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: '360px', background: '#fff', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e0e0e0', zIndex: 1000, overflow: 'hidden', maxHeight: '480px', overflowY: 'auto' }}>
                    {searchLoading ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>Searching...</div>
                    ) : (searchResults.users.length === 0 && searchResults.cases.length === 0) ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#bbb', fontSize: '13px' }}>No results for "{searchQuery}"</div>
                    ) : (
                      <>
                        {searchResults.users.length > 0 && (
                          <div>
                            <div style={{ padding: '10px 16px 6px', fontSize: '10px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', borderBottom: '1px solid #f0f0f0' }}>Users ({searchResults.users.length})</div>
                            {searchResults.users.map(function(u) {
                              var roleColors2 = { admin: '#e74c3c', healer: '#d4ac0d', patient: '#2980b9', student: '#27ae60', general: '#666' };
                              return (
                                <div key={u._id} onClick={function() { setSearchOpen(false); setSearchQuery(''); setActiveTab('registrations'); }}
                                  style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                                  onMouseEnter={function(e) { e.currentTarget.style.background = '#f9faf7'; }}
                                  onMouseLeave={function(e) { e.currentTarget.style.background = '#fff'; }}>
                                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg,#4a8f82,#5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>
                                    {u.firstName ? u.firstName.charAt(0) : ''}{u.lastName ? u.lastName.charAt(0) : ''}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>{u.firstName} {u.lastName}</div>
                                    <div style={{ fontSize: '11px', color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                                  </div>
                                  <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '10px', background: '#f0f0f0', color: roleColors2[u.role] || '#666', fontWeight: '700' }}>{u.role}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {searchResults.cases.length > 0 && (
                          <div>
                            <div style={{ padding: '10px 16px 6px', fontSize: '10px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', borderBottom: '1px solid #f0f0f0' }}>Cases ({searchResults.cases.length})</div>
                            {searchResults.cases.map(function(req) {
                              var pName = req.patient ? req.patient.firstName + ' ' + req.patient.lastName : (req.manualPatientName || 'Manual');
                              return (
                                <div key={req._id} onClick={function() { setSearchOpen(false); setSearchQuery(''); setActiveTab('healing'); }}
                                  style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                                  onMouseEnter={function(e) { e.currentTarget.style.background = '#f9faf7'; }}
                                  onMouseLeave={function(e) { e.currentTarget.style.background = '#fff'; }}>
                                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>{req.condition}</div>
                                  <div style={{ fontSize: '11px', color: '#999', marginTop: '1px' }}>{pName} · {req.status?.replace('_', ' ')} · {req.totalSessionsCompleted} sessions</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            {!isMobile && (
              <div style={s.headerDate}>
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            )}
          </div>
        </div>
 
        {loading ? (
          <div style={s.loading}>Loading dashboard data...</div>
        ) : (
          <div>
            <PlanUpgradeBanner />
            <div style={s.statsGrid}>
              <StatCard title="Total Users" value={stats ? stats.totalUsers : 0} subtitle="All registered members" color="#4a8f82" />
              <StatCard title="Students" value={stats ? stats.totalStudents : 0} subtitle="Enrolled students" color="#8DC63F" />
              <StatCard title="Patients" value={stats ? stats.totalPatients : 0} subtitle="Active patients" color="#00AEEF" />
              <StatCard title="Healers" value={stats ? stats.totalHealers : 0} subtitle="Certified healers" color="#c9a84c" />
            </div>
 
            <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '24px' }}>
              <div style={{ display: 'inline-flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', minWidth: 'min-content' }}>
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'healing', label: 'Healing Requests (' + filteredRequests.length + ')' },
                  { key: 'healers', label: 'Healer Performance (' + healers.length + ')' },
                  { key: 'registrations', label: 'New Registrations (' + recentUsers.length + ')' },
                  { key: 'approvals', label: 'Pending Approvals' + (pendingApprovals.length > 0 ? ' (' + pendingApprovals.length + ')' : '') },
                  { key: 'classes',  label: 'Classes & Courses' },
                  { key: 'calendar', label: <><FiCalendar style={{ marginRight: 4, verticalAlign: 'middle' }} />Calendar</> },
                  { key: 'analytics', label: 'Analytics' },
                  { key: 'settings',  label: 'Settings'  },
                  { key: 'billing',   label: 'Billing & Plan' }
                ].map(function(tab) {
                  return (
                    <button key={tab.key} onClick={function() { setActiveTab(tab.key); }}
                      style={{ ...(activeTab === tab.key ? { ...s.tab, ...s.tabActive } : s.tab), whiteSpace: 'nowrap' }}>
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
 
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div style={s.contentGrid}>
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div style={s.cardTitle}>Upcoming Sessions</div>
                    <button onClick={function() { window.location.href = '/sessions'; }} style={s.cardLink}>View All</button>
                  </div>
                  <div style={s.cardBody}>
                    {sessions.length === 0 ? <div style={s.empty}>No upcoming sessions</div> :
                      sessions.map(function(session) {
                        return (
                          <div key={session._id} style={s.listItem}>
                            <div>
                              <div style={s.listItemTitle}>{session.title}</div>
                              <div style={s.listItemSub}>{new Date(session.date).toLocaleDateString('en-IN')} — {session.startTime}</div>
                            </div>
                            <span style={{ ...s.badge, background: '#eafaf1', color: '#27ae60' }}>{session.currentEnrollments}/{session.maxParticipants}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div style={s.cardTitle}>Pending Assignments</div>
                    <button onClick={function() { setActiveTab('healing'); }} style={s.cardLink}>View All</button>
                  </div>
                  <div style={s.cardBody}>
                    {pendingRequests.length === 0 ? <div style={s.empty}>No pending requests</div> :
                      pendingRequests.slice(0, 5).map(function(req) {
                        return (
                          <div key={req._id} style={s.listItem}>
                            <div>
                              <div style={s.listItemTitle}>{(req.patient && (req.patient.firstName || req.patient.lastName)) ? (req.patient.firstName + ' ' + req.patient.lastName).trim() : (req.manualPatientName || (req.patient && req.patient.email) || 'Unknown')}</div>
                              <div style={s.listItemSub}>{req.condition}</div>
                            </div>
                            <span style={{ ...s.badge, background: '#fef9e7', color: '#d4ac0d' }}>Unassigned</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div style={s.cardTitle}>Pending Verifications</div>
                    <button onClick={function() { window.location.href = '/course-completions'; }} style={s.cardLink}>View All</button>
                  </div>
                  <div style={s.cardBody}>
                    {pendingCompletions.length === 0 ? <div style={s.empty}>No pending verifications</div> :
                      pendingCompletions.map(function(comp) {
                        return (
                          <div key={comp._id} style={s.listItem}>
                            <div>
                              <div style={s.listItemTitle}>{comp.student ? comp.student.firstName + ' ' + comp.student.lastName : ''}</div>
                              <div style={s.listItemSub}>{comp.courseName}</div>
                            </div>
                            <span style={{ ...s.badge, background: '#eaf4fb', color: '#2980b9' }}>Review</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}
 
            {/* HEALING REQUESTS TAB */}
            {activeTab === 'healing' && (
              <div>
                {/* Filter Bar */}
                <div style={s.filterBar}>
                  <div style={s.filterLeft}>
                    <div style={s.filterTitle}>Filters</div>
                    <div style={s.filterInputs}>
 
                      {/* Date From */}
                      <div style={s.filterField}>
                        <label style={s.filterLabel}>From</label>
                        <input style={s.filterInput} type="date" value={dateFilter.from}
                          onChange={function(e) { setDateFilter({ ...dateFilter, from: e.target.value }); }} />
                      </div>
 
                      {/* Date To */}
                      <div style={s.filterField}>
                        <label style={s.filterLabel}>To</label>
                        <input style={s.filterInput} type="date" value={dateFilter.to}
                          onChange={function(e) { setDateFilter({ ...dateFilter, to: e.target.value }); }} />
                      </div>
 
                      {/* Healer Multiselect */}
                      <div style={s.filterField}>
                        <label style={s.filterLabel}>Healer</label>
                        <div style={{ position: 'relative' }}>
                          <button onClick={function() { setHealerDropdownOpen(!healerDropdownOpen); }} style={s.healerSelectBtn}>
                            {selectedHealers.length === 0
                              ? 'All Healers'
                              : selectedHealerNames.length <= 2
                                ? selectedHealerNames.join(', ')
                                : selectedHealerNames.length + ' selected'}
                            <span style={{ marginLeft: '8px', fontSize: '10px' }}>▼</span>
                          </button>
                          {healerDropdownOpen && (
                            <div style={s.healerDropdown}>
                              <div style={s.healerDropdownItem}
                                onClick={function() { toggleHealerSelection('unassigned'); }}>
                                <input type="checkbox" checked={selectedHealers.includes('unassigned')} readOnly
                                  style={{ marginRight: '8px', accentColor: '#4a8f82' }} />
                                <span style={{ fontSize: '13px', color: '#333' }}>Unassigned</span>
                              </div>
                              {healers.map(function(h) {
                                return (
                                  <div key={h._id} style={s.healerDropdownItem}
                                    onClick={function() { toggleHealerSelection(h._id); }}>
                                    <input type="checkbox" checked={selectedHealers.includes(h._id)} readOnly
                                      style={{ marginRight: '8px', accentColor: '#4a8f82' }} />
                                    <span style={{ fontSize: '13px', color: '#333' }}>{h.firstName} {h.lastName}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
 
                      {/* Tag filter */}
                      <div style={s.filterField}>
                        <label style={s.filterLabel}>Tag</label>
                        <select value={filterTag} onChange={function(e) { setFilterTag(e.target.value); }} style={{ ...s.filterInput, minWidth: '120px' }}>
                          <option value="">All Tags</option>
                          {PRESET_TAGS.map(function(t) {
                            return <option key={t.label} value={t.label}>{t.label}</option>;
                          })}
                        </select>
                      </div>

                      <button onClick={handleApplyFilter} style={s.filterBtn}>Apply</button>
                      {(filterActive || filterTag) && <button onClick={handleClearFilter} style={s.clearBtn}>Clear</button>}
                    </div>
                  </div>
 
                  {/* Filter Stats */}
                  <div style={s.filterStats}>
                    <div style={s.filterStat}>
                      <div style={s.filterStatNum}>{filteredRequests.length}</div>
                      <div style={s.filterStatLabel}>Total</div>
                    </div>
                    <div style={s.filterStat}>
                      <div style={{ ...s.filterStatNum, color: '#d4ac0d' }}>{pendingRequests.length}</div>
                      <div style={s.filterStatLabel}>Pending</div>
                    </div>
                    <div style={s.filterStat}>
                      <div style={{ ...s.filterStatNum, color: '#27ae60' }}>{activeRequests.length}</div>
                      <div style={s.filterStatLabel}>Active</div>
                    </div>
                    <div style={s.filterStat}>
                      <div style={{ ...s.filterStatNum, color: '#666' }}>{completedRequests.length}</div>
                      <div style={s.filterStatLabel}>Completed</div>
                    </div>
                    <div style={s.filterStat}>
                      <div style={{ ...s.filterStatNum, color: '#4a8f82' }}>₹{totalRevenue.toLocaleString('en-IN')}</div>
                      <div style={s.filterStatLabel}>Revenue</div>
                    </div>
                    <div style={s.filterStat}>
                      <div style={{ ...s.filterStatNum, color: '#c9a84c' }}>₹{totalPaidToHealers.toLocaleString('en-IN')}</div>
                      <div style={s.filterStatLabel}>Paid to Healers</div>
                    </div>
                  </div>
                </div>
 
                {/* Requests List */}
                {filteredRequests.length === 0 ? (
                  <div style={s.card}><div style={s.empty}>No requests found for selected filters.</div></div>
                ) : filteredRequests.map(function(req) {
                  return (
                    <div key={req._id} style={s.requestCard}>
                      <div style={s.requestHeader}>
                        <div style={s.requestHeaderLeft}>
                          <div style={s.patientAvatar}>
                            {req.patient && req.patient.firstName ? req.patient.firstName.charAt(0) : (req.manualPatientName ? req.manualPatientName.charAt(0) : '?')}
                            {req.patient && req.patient.lastName ? req.patient.lastName.charAt(0) : (req.manualPatientName ? (req.manualPatientName.split(' ')[1] || '').charAt(0) : '')}
                          </div>
                          {(function() {
                            var pName = (req.patient && (req.patient.firstName || req.patient.lastName))
                              ? (req.patient.firstName + ' ' + req.patient.lastName).trim()
                              : (req.manualPatientName || (req.patient && req.patient.email) || 'Unknown');
                            var pContact = req.patient
                              ? [req.patient.email, req.patient.phone].filter(Boolean).join(' | ')
                              : (req.manualPatientName ? 'Manual entry — no account' : '');
                            return (
                              <div>
                                <div style={s.patientName}>
                                  {pName}
                                  {req.createdByAdmin && <span style={{ marginLeft: '8px', fontSize: '10px', background: '#eaf4fb', color: '#2980b9', padding: '2px 8px', borderRadius: '10px', fontWeight: '700' }}>Admin Created</span>}
                                  {!req.isForSelf && req.beneficiaryName && (
                                    <span style={s.beneficiaryTag}>requesting for {req.beneficiaryName}</span>
                                  )}
                                </div>
                                <div style={s.patientContact}>{pContact}</div>
                                <div style={s.requestCondition}>{req.condition}</div>
                              </div>
                            );
                          })()}
                        </div>
                        <div style={s.requestHeaderRight}>
                          <div style={s.requestBadges}>
                            <span style={{ ...s.badge, background: urgencyColors[req.urgency] ? urgencyColors[req.urgency].bg : '#f0f0f0', color: urgencyColors[req.urgency] ? urgencyColors[req.urgency].color : '#666' }}>{req.urgency}</span>
                            <span style={{ ...s.badge, background: statusColors[req.status] ? statusColors[req.status].bg : '#f0f0f0', color: statusColors[req.status] ? statusColors[req.status].color : '#666' }}>{req.status ? req.status.replace('_', ' ') : ''}</span>
                            {req.medicalReportsShared && <span style={{ ...s.badge, background: '#eafaf1', color: '#25D366' }}>Reports Shared</span>}
                          </div>
                          <div style={s.requestDate}>{new Date(req.requestedAt).toLocaleDateString('en-IN')}</div>
                        </div>
                      </div>
 
                      <div style={s.requestInfoRow}>
                        <div style={s.requestInfoBox}>
                          <div style={s.requestInfoLabel}>Assigned Healer</div>
                          <div style={s.requestInfoValue}>{req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : <span style={{ color: '#e74c3c' }}>Unassigned</span>}</div>
                        </div>
                        <div style={s.requestInfoBox}>
                          <div style={s.requestInfoLabel}>Sessions Done</div>
                          <div style={s.requestInfoValue}>{req.totalSessionsCompleted}</div>
                        </div>
                        <div style={s.requestInfoBox}>
                          <div style={s.requestInfoLabel}>Amount Received</div>
                          <div style={s.requestInfoValue}>
                            {req.payment && req.payment.amountReceived
                              ? <span style={{ color: '#27ae60' }}>₹{(req.payment.amountPaid || 0).toLocaleString('en-IN')}</span>
                              : <span style={{ color: '#d4ac0d' }}>Pending</span>}
                          </div>
                        </div>
                        {req.payment && req.payment.confirmationMethod === 'patient_self' && req.payment.paymentMethod && (
                          <div style={s.requestInfoBox}>
                            <div style={s.requestInfoLabel}>Patient Declared</div>
                            <div style={s.requestInfoValue}>
                              <span style={{ color: '#7053E2', fontWeight: '600' }}>
                                {req.payment.paymentMethod === 'cash' ? '💵 Cash at Centre' : '📱 GPay / UPI'}
                              </span>
                            </div>
                          </div>
                        )}
                        <div style={s.requestInfoBox}>
                          <div style={s.requestInfoLabel}>Healer Share</div>
                          <div style={s.requestInfoValue}>
                            {req.payment && req.payment.healerSharePercent
                              ? req.payment.healerSharePercent + '% — ₹' + (req.payment.healerShareAmount || 0).toLocaleString('en-IN')
                              : 'Not set'}
                          </div>
                        </div>
                        <div style={s.requestInfoBox}>
                          <div style={s.requestInfoLabel}>Settled to Healer</div>
                          <div style={s.requestInfoValue}>
                            {req.payment && req.payment.healerShareAmount > 0 ? (() => {
                              var totalSettled = (req.healerPayments || []).filter(function(p) { return p.status === 'settled'; }).reduce(function(s, p) { return s + (p.amountClaimed || 0); }, 0);
                              var total = req.payment.healerShareAmount || 0;
                              var fullySettled = totalSettled >= total;
                              return (
                                <span style={{ color: fullySettled ? '#27ae60' : totalSettled > 0 ? '#2980b9' : '#d4ac0d' }}>
                                  {fullySettled ? '✓ ' : ''}₹{totalSettled.toLocaleString('en-IN')} of ₹{total.toLocaleString('en-IN')}
                                  {fullySettled ? ' — Fully Settled' : totalSettled > 0 ? ' — Partial' : ' — Pending'}
                                </span>
                              );
                            })() : <span style={{ color: '#999' }}>—</span>}
                          </div>
                        </div>
                      </div>
 
                      <div style={s.requestActions}>
                        <button onClick={function() { window.location.href = '/healing-requests'; }} style={s.actionBtn}>Manage</button>
                        <button onClick={function() { paymentEdit === req._id ? setPaymentEdit(null) : openPaymentEdit(req); }} style={s.paymentBtn}>
                          {paymentEdit === req._id ? 'Close Payment' : 'Payment Details'}
                        </button>
                        {/* ── Tags row ── */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '700', color: '#bbb', textTransform: 'uppercase', letterSpacing: '1px', marginRight: '2px' }}>Tags:</span>
                          {(req.tags || []).map(function(tag) {
                            var tagDef = PRESET_TAGS.find(function(t) { return t.label === tag; }) || { color: '#666', bg: '#f0f0f0' };
                            return (
                              <span key={tag} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', fontWeight: '700', background: tagDef.bg, color: tagDef.color, border: '1px solid ' + tagDef.color + '50' }}>
                                {tag}
                              </span>
                            );
                          })}
                          <button onClick={function() { setTagEditorOpen(tagEditorOpen === req._id ? null : req._id); }}
                            style={{ fontSize: '10px', padding: '3px 10px', borderRadius: '12px', background: tagEditorOpen === req._id ? '#4a8f82' : 'transparent', color: tagEditorOpen === req._id ? '#fff' : '#aaa', border: '1px dashed #ccc', cursor: 'pointer', fontWeight: '700' }}>
                            {tagEditorOpen === req._id ? '✕ Close' : '+ Add Tag'}
                          </button>
                        </div>
                        {/* Inline tag picker */}
                        {tagEditorOpen === req._id && (
                          <div style={{ marginBottom: '12px', background: '#f9faf7', border: '1px solid #e0e8e0', borderRadius: '8px', padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {PRESET_TAGS.map(function(tagDef) {
                              var active = (req.tags || []).includes(tagDef.label);
                              return (
                                <button key={tagDef.label}
                                  disabled={savingTags}
                                  onClick={function() { handleToggleTag(req, tagDef.label); }}
                                  style={{ fontSize: '12px', padding: '5px 14px', borderRadius: '14px', cursor: savingTags ? 'not-allowed' : 'pointer', fontWeight: '700', border: '1.5px solid ' + tagDef.color, background: active ? tagDef.bg : '#fff', color: tagDef.color, opacity: savingTags ? 0.6 : 1 }}>
                                  {active ? '✓ ' : ''}{tagDef.label}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        <button onClick={function() { setExpandedRequest(expandedRequest === req._id ? null : req._id); }} style={s.expandBtn}>
                          {expandedRequest === req._id ? 'Hide History' : 'View History (' + (req.sessions ? req.sessions.length : 0) + ')'}
                        </button>
                      </div>
 
                      {paymentEdit === req._id && (
                        <div style={s.paymentPanel}>
                          <div style={s.paymentPanelTitle}>Payment Details</div>
                          {/* Promo Code */}
                          <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
                            <label style={s.paymentLabel}>Promo Code</label>
                            {req.promoApplied && req.promoApplied.code ? (
                              <div style={{ marginTop: '8px', fontSize: '13px', color: '#27ae60', fontWeight: '600' }}>
                                ✓ Promo applied: {req.promoApplied.code} — ₹{(req.promoApplied.discountAmount || 0).toLocaleString('en-IN')} off
                              </div>
                            ) : (
                              <>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                  <input
                                    style={{ ...s.paymentInput, flex: '1', width: 'auto' }}
                                    value={promoInput}
                                    onChange={function(e) { setPromoInput(e.target.value); setPromoResult(null); }}
                                    placeholder="Enter code..."
                                    disabled={!!(promoResult && !promoResult.error)}
                                  />
                                  <button
                                    onClick={function() { applyPromo(req._id); }}
                                    style={s.savePaymentBtn}
                                    disabled={applyingPromo || !promoInput.trim() || !!(promoResult && !promoResult.error)}
                                  >
                                    {applyingPromo ? 'Applying...' : 'Apply'}
                                  </button>
                                </div>
                                {promoResult && !promoResult.error && (
                                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#27ae60', fontWeight: '600' }}>
                                    ✓ Discount applied — ₹{(promoResult.discountAmount || 0).toLocaleString('en-IN')} off, new total ₹{(promoResult.finalAmount || 0).toLocaleString('en-IN')}
                                  </div>
                                )}
                                {promoResult && promoResult.error && (
                                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#e74c3c' }}>
                                    {promoResult.error}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div style={s.paymentGrid}>
                            <div style={s.paymentField}>
                              <label style={s.paymentLabel}>Amount Received</label>
                              <div style={s.toggleRow}>
                                <button type="button" onClick={function() { setPaymentForm({ ...paymentForm, amountReceived: true }); }} style={paymentForm.amountReceived ? s.toggleBtnActive : s.toggleBtn}>Yes</button>
                                <button type="button" onClick={function() { setPaymentForm({ ...paymentForm, amountReceived: false }); }} style={!paymentForm.amountReceived ? s.toggleBtnActive : s.toggleBtn}>No</button>
                              </div>
                            </div>
                            <div style={s.paymentField}>
                              <label style={s.paymentLabel}>Amount Paid (₹)</label>
                              <input style={s.paymentInput} type="number" value={paymentForm.amountPaid} onChange={function(e) { setPaymentForm({ ...paymentForm, amountPaid: Number(e.target.value) }); }} min="0" />
                            </div>
                            <div style={s.paymentField}>
                              <label style={s.paymentLabel}>Healer Share (%)</label>
                              <input style={s.paymentInput} type="number" value={paymentForm.healerSharePercent} onChange={function(e) { setPaymentForm({ ...paymentForm, healerSharePercent: Number(e.target.value) }); }} min="0" max="100" />
                            </div>
                            <div style={s.paymentField}>
                              <label style={s.paymentLabel}>Amount to Healer (Auto)</label>
                              <div style={s.paymentCalcValue}>₹{healerShareAmount.toLocaleString('en-IN')}</div>
                            </div>
                            <div style={{ ...s.paymentField, gridColumn: '1 / -1' }}>
                              <label style={s.paymentLabel}>Notes (optional)</label>
                              <input style={s.paymentInput} value={paymentForm.notes} onChange={function(e) { setPaymentForm({ ...paymentForm, notes: e.target.value }); }} placeholder="Payment notes..." />
                            </div>
                          </div>
                          <button onClick={function() { savePayment(req._id); }} style={s.savePaymentBtn} disabled={savingPayment}>
                            {savingPayment ? 'Saving...' : 'Save Payment Details'}
                          </button>

                          {/* Patient Invoice */}
                          {req.payment && req.payment.amountReceived && req.payment.amountPaid > 0 && (
                            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>Patient Invoice</div>
                              {req.payment.billNumber && (
                                <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
                                  Bill No: <strong>{req.payment.billNumber}</strong>
                                  {req.payment.billGeneratedAt && <span style={{ color: '#999', marginLeft: '8px' }}>({new Date(req.payment.billGeneratedAt).toLocaleDateString('en-IN')})</span>}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button onClick={function() { handleGeneratePatientInvoice(req._id); }} style={{ ...s.savePaymentBtn, background: '#2980b9' }} disabled={generatingInvoice}>
                                  {generatingInvoice ? 'Generating...' : req.payment.billNumber ? 'Resend Invoice' : 'Generate & Email Invoice'}
                                </button>
                                <button onClick={function() { printPatientInvoice(req); }} style={{ ...s.savePaymentBtn, background: '#4a8f82' }}>
                                  Print / Download PDF
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Healer Payments */}
                          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Healer Payments</div>
                              {req.assignedHealer && (req.healerPayments || []).length > 0 && (
                                <button onClick={function() { printHealerStatement(req); }} style={{ fontSize: '11px', padding: '5px 12px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700' }}>
                                  Print Statement
                                </button>
                              )}
                            </div>
                            {req.payment && req.payment.healerShareAmount > 0 && (
                              <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                                Total entitlement: <strong>₹{(req.payment.healerShareAmount || 0).toLocaleString('en-IN')}</strong>
                                {req.sessions_planned && <span style={{ color: '#999', marginLeft: '8px' }}>(₹{Math.round((req.payment.healerShareAmount || 0) / req.sessions_planned).toLocaleString('en-IN')}/session)</span>}
                              </div>
                            )}
                            {(!req.healerPayments || req.healerPayments.length === 0) ? (
                              <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>No healer payments yet.</div>
                            ) : req.healerPayments.map(function(hp, hIdx) {
                              return (
                                <div key={hIdx} style={{ background: hp.status === 'settled' ? '#f0faf4' : '#fef9e7', border: '1px solid ' + (hp.status === 'settled' ? '#a9dfbf' : '#f0c040'), borderRadius: '6px', padding: '10px 12px', marginBottom: '8px', fontSize: '12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                                    <div>
                                      <strong>{hp.paymentNumber}</strong>
                                      <span style={{ marginLeft: '8px', fontSize: '11px', background: hp.status === 'settled' ? '#27ae60' : '#d4ac0d', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>{hp.status}</span>
                                      <span style={{ marginLeft: '6px', color: '#999', fontSize: '11px' }}>{hp.raisedBy === 'admin' ? '(Admin)' : '(Healer)'}</span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div>{hp.sessionsClaimed} session(s) — <strong>₹{(hp.amountClaimed || 0).toLocaleString('en-IN')}</strong></div>
                                      <div style={{ color: '#999', fontSize: '11px' }}>{new Date(hp.raisedAt).toLocaleDateString('en-IN')}</div>
                                    </div>
                                  </div>
                                  {hp.notes && <div style={{ color: '#555', marginTop: '4px' }}>{hp.notes}</div>}
                                  {hp.status === 'pending' && (
                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                      <select value={settlePatientNameDisplay} onChange={function(e) { setSettlePatientNameDisplay(e.target.value); }} style={{ fontSize: '11px', padding: '3px 6px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <option value="full">Full name on statement</option>
                                        <option value="initials">Initials only</option>
                                      </select>
                                      <button onClick={function() { handleSettleHealerPayment(req._id, hp._id); }} disabled={settlingPaymentId === hp._id} style={{ fontSize: '11px', padding: '4px 10px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                        {settlingPaymentId === hp._id ? 'Settling...' : 'Mark Settled & Send Statement'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <button onClick={function() { setSettlementModal(req._id); setSettlementForm({ sessionsClaimed: 1, notes: '', patientNameDisplay: 'full' }); }} style={{ fontSize: '12px', padding: '6px 14px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '4px' }}>
                              + Add Direct Settlement
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Settlement Modal */}
                      {settlementModal === req._id && (
                        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                          <div style={{ background: '#fff', borderRadius: '8px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '20px' }}>Add Direct Settlement</div>
                            <div style={{ marginBottom: '14px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Sessions Claimed</label>
                              <input type="number" min="1" value={settlementForm.sessionsClaimed} onChange={function(e) { setSettlementForm({ ...settlementForm, sessionsClaimed: parseInt(e.target.value) || 1 }); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ marginBottom: '14px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Notes (optional)</label>
                              <input value={settlementForm.notes} onChange={function(e) { setSettlementForm({ ...settlementForm, notes: e.target.value }); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} placeholder="Payment notes..." />
                            </div>
                            <div style={{ marginBottom: '20px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Patient Name on Statement</label>
                              <select value={settlementForm.patientNameDisplay} onChange={function(e) { setSettlementForm({ ...settlementForm, patientNameDisplay: e.target.value }); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}>
                                <option value="full">Full Name</option>
                                <option value="initials">Initials Only</option>
                              </select>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button onClick={function() { handleAdminSettlement(req._id); }} disabled={savingSettlement} style={{ flex: 1, padding: '10px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                                {savingSettlement ? 'Processing...' : 'Settle & Send Statement'}
                              </button>
                              <button onClick={function() { setSettlementModal(null); }} style={{ padding: '10px 16px', background: '#f5f5f5', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                            </div>
                          </div>
                        </div>
                      )}
 
                      {expandedRequest === req._id && (
                        <div style={s.sessionHistory}>
                          <div style={s.sessionHistoryTitle}>Session History ({req.sessions ? req.sessions.length : 0} sessions)</div>
                          {!req.sessions || req.sessions.length === 0 ? (
                            <div style={{ padding: '16px', color: '#999', fontSize: '13px' }}>No sessions recorded yet.</div>
                          ) : req.sessions.map(function(session, index) {
                            return (
                              <div key={index} style={s.sessionItem}>
                                <div style={s.sessionItemLeft}>
                                  <div style={s.sessionNum}>#{index + 1}</div>
                                  <div>
                                    <div style={s.sessionDate}>{new Date(session.date).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</div>
                                    <div style={s.sessionDuration}>{session.duration} minutes</div>
                                  </div>
                                </div>
                                <div style={s.sessionRemarks}>{session.healerRemarks || 'No remarks'}</div>
                                <span style={{ ...s.badge, background: session.status === 'completed' ? '#eafaf1' : '#fef9e7', color: session.status === 'completed' ? '#27ae60' : '#d4ac0d' }}>{session.status}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
 
            {/* HEALER PERFORMANCE TAB */}
            {activeTab === 'healers' && (
              <div>
                {/* Date Filter for Healer Performance */}
                <div style={s.healerPerfFilterBar}>
                  <div style={s.filterTitle}>Filter by Date</div>
                  <div style={s.filterInputs}>
                    <div style={s.filterField}>
                      <label style={s.filterLabel}>From</label>
                      <input style={s.filterInput} type="date" value={healerPerfDateFilter.from}
                        onChange={function(e) { setHealerPerfDateFilter({ ...healerPerfDateFilter, from: e.target.value }); }} />
                    </div>
                    <div style={s.filterField}>
                      <label style={s.filterLabel}>To</label>
                      <input style={s.filterInput} type="date" value={healerPerfDateFilter.to}
                        onChange={function(e) { setHealerPerfDateFilter({ ...healerPerfDateFilter, to: e.target.value }); }} />
                    </div>
                    <button onClick={function() { setHealerPerfFilterActive(true); }} style={s.filterBtn}>Apply</button>
                    {healerPerfFilterActive && (
                      <button onClick={function() { setHealerPerfDateFilter({ from: '', to: '' }); setHealerPerfFilterActive(false); }} style={s.clearBtn}>Clear</button>
                    )}
                  </div>
                  {healerPerfFilterActive && (healerPerfDateFilter.from || healerPerfDateFilter.to) && (
                    <div style={s.filterActiveNote}>
                      Showing stats for: {healerPerfDateFilter.from || 'start'} → {healerPerfDateFilter.to || 'today'}
                    </div>
                  )}
                </div>
 
                {healers.length === 0 ? (
                  <div style={s.card}><div style={s.empty}>No healers found.</div></div>
                ) : (
                  <div style={s.healerGrid}>
                    {healers.map(function(healer) {
                      const hStats = getHealerStats(healer._id);
                      return (
                        <div key={healer._id} style={s.healerCard}>
                          <div style={s.healerCardHeader}>
                            <div style={s.healerAvatar}>{healer.firstName ? healer.firstName.charAt(0) : ''}{healer.lastName ? healer.lastName.charAt(0) : ''}</div>
                            <div>
                              <div style={s.healerName}>{healer.firstName} {healer.lastName}</div>
                              <div style={s.healerEmail}>{healer.email}</div>
                              <button
                                disabled={togglingAvailId === healer._id}
                                onClick={async function() {
                                  setTogglingAvailId(healer._id);
                                  try {
                                    await updateHealerVisibility(healer._id, { isAvailable: !healer.isAvailable });
                                    setHealers(function(prev) {
                                      return prev.map(function(h) {
                                        return h._id === healer._id ? Object.assign({}, h, { isAvailable: !h.isAvailable }) : h;
                                      });
                                    });
                                  } catch(e) {
                                    alert('Could not update availability');
                                  } finally {
                                    setTogglingAvailId(null);
                                  }
                                }}
                                style={{ background: healer.isAvailable ? '#eafaf1' : '#fdf0f0', color: healer.isAvailable ? '#27ae60' : '#e74c3c', border: '1px solid', borderColor: healer.isAvailable ? '#a9dfbf' : '#f5b7b1', borderRadius: '10px', padding: '2px 10px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', letterSpacing: '0.5px' }}
                              >
                                {togglingAvailId === healer._id ? '...' : healer.isAvailable ? 'Available' : 'Unavailable'}
                              </button>
                            </div>
                          </div>
 
                          {/* Stats Row */}
                          <div style={s.healerStats}>
                            <div style={s.healerStat}>
                              <div style={s.healerStatValue}>{hStats.total}</div>
                              <div style={s.healerStatLabel}>Total</div>
                            </div>
                            <div style={s.healerStat}>
                              <div style={{ ...s.healerStatValue, color: '#27ae60' }}>{hStats.active}</div>
                              <div style={s.healerStatLabel}>Active</div>
                            </div>
                            <div style={s.healerStat}>
                              <div style={{ ...s.healerStatValue, color: '#666' }}>{hStats.completed}</div>
                              <div style={s.healerStatLabel}>Done</div>
                            </div>
                            <div style={s.healerStat}>
                              <div style={{ ...s.healerStatValue, color: '#2980b9' }}>{hStats.totalSessions}</div>
                              <div style={s.healerStatLabel}>Sessions</div>
                            </div>
                          </div>
 
                          {/* Revenue Row */}
                          <div style={s.healerFinancials}>
                            <div style={s.healerFinancialBox}>
                              <div style={s.healerFinancialLabel}>Revenue Generated</div>
                              <div style={s.healerFinancialValue}>₹{hStats.revenueGenerated.toLocaleString('en-IN')}</div>
                            </div>
                            <div style={{ ...s.healerFinancialBox, borderLeft: '1px solid #e0e0e0' }}>
                              <div style={s.healerFinancialLabel}>Paid to Healer</div>
                              <div style={{ ...s.healerFinancialValue, color: hStats.paidToHealer > 0 ? '#27ae60' : '#d4ac0d' }}>
                                ₹{hStats.paidToHealer.toLocaleString('en-IN')}
                                {hStats.revenueGenerated > 0 && hStats.paidToHealer === 0 && (
                                  <span style={{ fontSize: '10px', color: '#d4ac0d', display: 'block', fontWeight: '600', marginTop: '2px' }}>Pending Settlement</span>
                                )}
                              </div>
                            </div>
                          </div>
 
                          {/* Assigned Patients */}
                          {healingRequests.filter(function(r) { return r.assignedHealer && r.assignedHealer._id === healer._id; }).length > 0 && (
                            <div style={s.healerPatients}>
                              <div style={s.healerPatientsTitle}>Assigned Patients</div>
                              {healingRequests.filter(function(r) { return r.assignedHealer && r.assignedHealer._id === healer._id; }).map(function(req) {
                                return (
                                  <div key={req._id} style={s.healerPatientItem}>
                                    <div>
                                      <div style={s.healerPatientName}>{req.patient ? req.patient.firstName + ' ' + req.patient.lastName : ''}</div>
                                      <div style={s.healerPatientCondition}>{req.condition} | {req.totalSessionsCompleted} sessions</div>
                                    </div>
                                    <span style={{ ...s.badge, background: statusColors[req.status] ? statusColors[req.status].bg : '#f0f0f0', color: statusColors[req.status] ? statusColors[req.status].color : '#666' }}>
                                      {req.status ? req.status.replace('_', ' ') : ''}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
 

            {/* REGISTRATIONS TAB */}
            {activeTab === 'approvals' && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div>
                    <div style={s.cardTitle}>Pending Approvals</div>
                    <div style={s.cardSub}>Self-registered users awaiting admin approval</div>
                  </div>
                </div>
                {pendingApprovals.length === 0 ? (
                  <div style={s.empty}>No pending registrations</div>
                ) : (
                  <table style={s.table}>
                    <thead>
                      <tr style={s.tableHead}>
                        <th style={s.th}>Name</th>
                        <th style={s.th}>Email</th>
                        <th style={s.th}>Phone</th>
                        <th style={s.th}>Role</th>
                        <th style={s.th}>Registered</th>
                        <th style={s.th}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingApprovals.map(function(user) {
                        return (
                          <tr key={user._id} style={s.tableRow}>
                            <td style={s.td}>
                              <div style={s.userCell}>
                                <div style={{ ...s.avatar, background: '#d4ac0d' }}>
                                  {user.firstName ? user.firstName.charAt(0) : ''}{user.lastName ? user.lastName.charAt(0) : ''}
                                </div>
                                <div style={s.tdTitle}>{user.firstName} {user.lastName}</div>
                              </div>
                            </td>
                            <td style={s.td}>{user.email}</td>
                            <td style={s.td}>{user.phone || '—'}</td>
                            <td style={s.td}>
                              <span style={{ ...s.badge, background: '#fef9e7', color: '#d4ac0d' }}>{user.role}</span>
                            </td>
                            <td style={s.td}>{new Date(user.createdAt).toLocaleDateString('en-IN')}</td>
                            <td style={s.td}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  disabled={approvingId === user._id}
                                  onClick={async function() {
                                    setApprovingId(user._id);
                                    try {
                                      await approveUser(user._id);
                                      setPendingApprovals(function(prev) { return prev.filter(function(u) { return u._id !== user._id; }); });
                                    } catch(e) { alert('Failed to approve'); }
                                    setApprovingId(null);
                                  }}
                                  style={{ ...s.editBtn, background: '#27ae60', color: '#fff', border: 'none' }}>
                                  {approvingId === user._id ? '...' : 'Approve'}
                                </button>
                                <button
                                  disabled={approvingId === user._id}
                                  onClick={async function() {
                                    if (!window.confirm('Reject and delete this registration?')) return;
                                    setApprovingId(user._id);
                                    try {
                                      await rejectUser(user._id);
                                      setPendingApprovals(function(prev) { return prev.filter(function(u) { return u._id !== user._id; }); });
                                    } catch(e) { alert('Failed to reject'); }
                                    setApprovingId(null);
                                  }}
                                  style={{ ...s.editBtn, background: '#e74c3c', color: '#fff', border: 'none' }}>
                                  Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'registrations' && (
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div>
                    <div style={s.cardTitle}>Recent Registrations</div>
                    <div style={s.cardSub}>Latest members — review and assign roles</div>
                  </div>
                  <button onClick={function() { window.location.href = '/users'; }} style={s.actionBtn}>Manage All Users</button>
                </div>
                <table style={s.table}>
                  <thead>
                    <tr style={s.tableHead}>
                      <th style={s.th}>Name</th>
                      <th style={s.th}>Email</th>
                      <th style={s.th}>Phone</th>
                      <th style={s.th}>Role</th>
                      <th style={s.th}>Registered</th>
                      <th style={s.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentUsers.map(function(user) {
                      return (
                        <tr key={user._id} style={s.tableRow}>
                          <td style={s.td}>
                            <div style={s.userCell}>
                              <div style={{ ...s.avatar, background: roleColors[user.role] ? roleColors[user.role].color : '#4a8f82' }}>
                                {user.firstName ? user.firstName.charAt(0) : ''}{user.lastName ? user.lastName.charAt(0) : ''}
                              </div>
                              <div style={s.tdTitle}>{user.firstName} {user.lastName}</div>
                            </div>
                          </td>
                          <td style={s.td}>{user.email}</td>
                          <td style={s.td}>{user.phone}</td>
                          <td style={s.td}>
                            <span style={{ ...s.badge, background: roleColors[user.role] ? roleColors[user.role].bg : '#f0f0f0', color: roleColors[user.role] ? roleColors[user.role].color : '#666' }}>{user.role}</span>
                          </td>
                          <td style={s.td}>{new Date(user.createdAt).toLocaleDateString('en-IN')}</td>
                          <td style={s.td}>
                            <button onClick={function() { window.location.href = '/users'; }} style={s.editBtn}>Edit Role</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'classes' && (
              <ClassManager />
            )}

            {/* CALENDAR TAB — Phase 11 Task 11.1-G — lazy: data fetched only when tab active */}
            {activeTab === 'calendar' && (
              <div style={s.card}>
                <HolisticCalendar />
              </div>
            )}

            {/* ANALYTICS TAB — Phase 11 Sprint 11-S7 */}
            {activeTab === 'analytics' && (
              <div style={s.card}>
                <AssignmentAnalytics />
              </div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === 'settings' && (
              <div style={s.card}>
                {/* Centre Code — read-only */}
                <div style={{ padding: '24px', borderBottom: '1px solid #e8f0e8' }}>
                  <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' }}>Centre Code</div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '16px' }}>Share this code with healers and patients to join your centre</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ background: '#f5f8f5', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '10px 16px', fontFamily: 'monospace', fontSize: '18px', fontWeight: '700', letterSpacing: '3px', color: '#1a1a2e', minWidth: '100px', textAlign: 'center' }}>
                      {centreCode || '—'}
                    </div>
                    <button
                      type="button"
                      onClick={function() {
                        if (!centreCode) return;
                        navigator.clipboard.writeText(centreCode);
                        setCodeCopied(true);
                        setTimeout(function() { setCodeCopied(false); }, 2000);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', border: '1px solid #e0e0e0', borderRadius: '6px', background: '#fff', color: codeCopied ? '#27ae60' : '#4a8f82', cursor: 'pointer', fontSize: '13px', fontWeight: '600', minHeight: '44px', fontFamily: "'Open Sans', sans-serif" }}
                    >
                      {codeCopied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                      {codeCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {showListingConfirm && (
                  <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '28px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px', fontFamily: "'Raleway', sans-serif" }}>Enable directory listing?</div>
                      <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '24px' }}>This will make your centre visible on the public directory. Continue?</div>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button type="button" onClick={function() { setShowListingConfirm(false); }}
                          style={{ padding: '10px 20px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
                          Cancel
                        </button>
                        <button type="button" disabled={listingSubmitting}
                          onClick={async function() {
                            setListingSubmitting(true);
                            try {
                              const res = await api.put('/admin/directory/listing', { isListed: true });
                              setDirectoryListing(res.data.data);
                            } catch (err) {
                              alert(err.response?.data?.message || 'Failed to update listing');
                            } finally {
                              setListingSubmitting(false);
                              setShowListingConfirm(false);
                            }
                          }}
                          style={{ padding: '10px 20px', borderRadius: '7px', border: 'none', background: listingSubmitting ? '#a0c4bf' : '#4a8f82', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: listingSubmitting ? 'not-allowed' : 'pointer', minHeight: '44px' }}>
                          {listingSubmitting ? 'Saving…' : 'Enable listing'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ padding: '24px' }}>
                  <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' }}>Directory listing</div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '24px' }}>Control your centre's visibility on find.lifechiai.com</div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#f9faf7', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                    <div>
                      <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '3px' }}>List on find.lifechiai.com</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Allow patients to find your centre on the public directory</div>
                    </div>
                    {/* Toggle — label provides 44×44px touch target */}
                    <label style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '44px', minHeight: '44px', cursor: listingSubmitting ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={!!(directoryListing?.isListed)}
                        disabled={listingSubmitting}
                        onChange={function() {
                          if (directoryListing?.isListed) {
                            setListingSubmitting(true);
                            api.put('/admin/directory/listing', { isListed: false })
                              .then(function(res) { setDirectoryListing(res.data.data); })
                              .catch(function(err) { alert(err.response?.data?.message || 'Failed to update listing'); })
                              .finally(function() { setListingSubmitting(false); });
                          } else {
                            setShowListingConfirm(true);
                          }
                        }}
                        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                      />
                      <span style={{ display: 'block', width: '48px', height: '26px', background: directoryListing?.isListed ? '#4a8f82' : '#ccc', borderRadius: '13px', position: 'relative', transition: 'background 0.2s', opacity: listingSubmitting ? 0.6 : 1 }}>
                        <span style={{ position: 'absolute', top: '3px', left: directoryListing?.isListed ? '25px' : '3px', width: '20px', height: '20px', background: '#fff', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </span>
                    </label>
                  </div>

                  {directoryListing?.isListed && directoryListing?.slug && (
                    <div style={{ marginTop: '12px', padding: '12px 16px', background: '#eafaf1', borderRadius: '6px', border: '1px solid #a9dfbf' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#27ae60', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Your listing</div>
                      <div style={{ fontSize: '13px', color: '#1a1a2e', fontFamily: 'monospace' }}>
                        find.lifechiai.com/find/{directoryListing.slug}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'settings' && (
              <AssignmentConfigSettings />
            )}
            {activeTab === 'billing' && (
              <BillingPage />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  main: { marginLeft: '260px', flex: 1, padding: '32px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' },
  headerTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
  headerSub: { fontSize: '13px', color: '#666', margin: 0 },
  headerDate: { fontSize: '13px', color: '#666', fontWeight: '600' },
  loading: { textAlign: 'center', padding: '40px', color: '#666', fontSize: '14px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '28px' },
  tabs: { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', marginBottom: '24px' },
  tab: { flex: 1, padding: '10px 16px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  tabActive: { background: '#4a8f82', color: '#fff' },
  contentGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #e0e0e0' },
  cardTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  cardSub: { fontSize: '12px', color: '#999', marginTop: '2px' },
  cardBody: { padding: '8px 0' },
  cardLink: { background: 'none', border: 'none', color: '#4a8f82', fontSize: '12px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase' },
  listItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderBottom: '1px solid #f5f5f5' },
  listItemTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
  listItemSub: { fontSize: '12px', color: '#666' },
  empty: { padding: '32px', textAlign: 'center', color: '#999', fontSize: '13px' },
 
  // Filter Bar
  filterBar: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px 24px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' },
  healerPerfFilterBar: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px 24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' },
  filterTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', whiteSpace: 'nowrap' },
  filterActiveNote: { fontSize: '12px', color: '#4a8f82', fontWeight: '600', marginLeft: '8px' },
  filterLeft: {},
  filterInputs: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' },
  filterField: { position: 'relative' },
  filterLabel: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginBottom: '4px', fontFamily: "'Raleway', sans-serif" },
  filterInput: { padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '13px', fontFamily: "'Open Sans', sans-serif", color: '#333' },
  filterBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px', alignSelf: 'flex-end' },
  clearBtn: { background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '4px', padding: '9px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', alignSelf: 'flex-end' },
 
  // Healer multiselect dropdown
  healerSelectBtn: { padding: '8px 14px', border: '1px solid #e0e0e0', borderRadius: '4px', fontFamily: "'Open Sans', sans-serif", fontSize: '13px', color: '#333', background: '#fff', cursor: 'pointer', minWidth: '160px', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  healerDropdown: { position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '220px', padding: '8px 0', marginTop: '4px' },
  healerDropdownItem: { display: 'flex', alignItems: 'center', padding: '8px 16px', cursor: 'pointer', ':hover': { background: '#f9faf7' } },
 
  // Filter Stats
  filterStats: { display: 'flex', gap: '20px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' },
  filterStat: { textAlign: 'center', minWidth: '60px' },
  filterStatNum: { fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '700', color: '#4a8f82', lineHeight: 1 },
  filterStatLabel: { fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' },
 
  // Request Cards
  requestCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', marginBottom: '16px' },
  requestHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  requestHeaderLeft: { display: 'flex', gap: '14px', alignItems: 'flex-start' },
  requestHeaderRight: { textAlign: 'right' },
  patientAvatar: { width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, #4a8f82, #5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  patientName: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '3px' },
  patientContact: { fontSize: '12px', color: '#666', marginBottom: '4px' },
  requestCondition: { fontSize: '13px', color: '#4a8f82', fontWeight: '600' },
  beneficiaryTag: { marginLeft: '8px', fontSize: '11px', color: '#c9a84c', fontStyle: 'italic', fontWeight: '600' },
  requestBadges: { display: 'flex', gap: '6px', justifyContent: 'flex-end', marginBottom: '6px', flexWrap: 'wrap' },
  requestDate: { fontSize: '12px', color: '#999' },
  requestInfoRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '16px' },
  requestInfoBox: { background: '#f9faf7', borderRadius: '6px', padding: '10px 12px' },
  requestInfoLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginBottom: '4px', fontFamily: "'Raleway', sans-serif" },
  requestInfoValue: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e' },
  requestActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  actionBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' },
  paymentBtn: { background: 'transparent', color: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '4px', padding: '8px 16px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' },
  expandBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px 16px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer' },
  editBtn: { background: 'none', border: '1px solid #4a8f82', color: '#4a8f82', borderRadius: '4px', padding: '6px 12px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer' },
 
  // Payment Panel
  paymentPanel: { marginTop: '16px', background: '#f9faf7', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' },
  paymentPanelTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' },
  paymentGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' },
  paymentField: {},
  paymentLabel: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '8px', fontFamily: "'Raleway', sans-serif" },
  paymentInput: { width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
  paymentCalcValue: { fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#4a8f82' },
  toggleRow: { display: 'flex', gap: '6px' },
  toggleBtn: { padding: '8px 16px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  toggleBtnActive: { padding: '8px 16px', background: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#fff', cursor: 'pointer' },
  savePaymentBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
 
  // Session History
  sessionHistory: { marginTop: '16px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' },
  sessionHistoryTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', padding: '12px 16px', background: '#f9faf7', borderBottom: '1px solid #e0e0e0' },
  sessionItem: { display: 'grid', gridTemplateColumns: '140px 1fr auto', gap: '16px', alignItems: 'start', padding: '14px 16px', borderBottom: '1px solid #f5f5f5' },
  sessionItemLeft: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  sessionNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#4a8f82', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  sessionDate: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '3px' },
  sessionDuration: { fontSize: '11px', color: '#999' },
  sessionRemarks: { fontSize: '13px', color: '#555', lineHeight: '1.6' },
 
  // Healer Performance
  healerGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' },
  healerCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px' },
  healerCardHeader: { display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '20px' },
  healerAvatar: { width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #4a8f82, #5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  healerName: { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  healerEmail: { fontSize: '12px', color: '#666', marginBottom: '8px' },
  healerStats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', padding: '16px', background: '#f9faf7', borderRadius: '6px', marginBottom: '12px' },
  healerStat: { textAlign: 'center' },
  healerStatValue: { fontFamily: "'Raleway', sans-serif", fontSize: '24px', fontWeight: '700', color: '#4a8f82', lineHeight: 1 },
  healerStatLabel: { fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' },
  healerFinancials: { display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: '16px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' },
  healerFinancialBox: { padding: '14px 16px', background: '#fff' },
  healerFinancialLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
  healerFinancialValue: { fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#4a8f82' },
  healerPatients: { borderTop: '1px solid #e0e0e0', paddingTop: '16px' },
  healerPatientsTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' },
  healerPatientItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f5f5f5' },
  healerPatientName: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
  healerPatientCondition: { fontSize: '11px', color: '#999' },
 
  // Table
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHead: { background: '#f9faf7' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#666', fontFamily: "'Raleway', sans-serif", borderBottom: '1px solid #e0e0e0' },
  tableRow: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '14px 16px', fontSize: '13px', color: '#333', verticalAlign: 'middle' },
  tdTitle: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', color: '#1a1a2e' },
  userCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  badge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }
};
 
export default AdminDashboard;